'use client';
/**
 * The header's "New request" dropdown — trigger + panel, extracted from
 * `components/common/Header.tsx`.
 *
 * Not to be confused with `./NewRequestDropdown.tsx`, which is the mobile
 * footer's version of the same actions (different, legacy visual language).
 *
 * Styling comes from `./menuStyles` (kit `TopHeaderMenu` / `TopHeaderMenuItem`
 * classes). It owns its own open state, click-outside and published-forms
 * fetch, so the header no longer carries them.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import * as LucideIcons from 'lucide-react';
import {
  Plus,
  CalendarPlus,
  FileText,
  Receipt,
  Wrench,
  ChevronRight,
} from 'lucide-react';
import { useAuthContext } from 'contexts/AuthContext';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { hasPermission } from 'utils/permissions';
import useClickOutside from 'hooks/useClickOutside';
import { getListPublishedForms } from 'features/forms/services/formServices';
import { FormsResponse } from 'features/forms/types';
import CreateConge from 'features/conge/components/CreateConge';
import CreateDocument from 'features/documents/components/CreateDocument';
import CreateExpenseForm from 'features/expenses/components/CreateExpenseForm';
import CreateIntervention from 'features/material/components/intervention/CreateIntervention';
import EditResponse from 'features/forms/components/FormResponses/EditResponse';
import SecondaryForms from 'features/forms/components/SecondaryForms';
import {
  MENU_PANEL_BODY_CLASSES,
  MENU_PANEL_CLASSES,
  MENU_PANEL_FOOTER_CLASSES,
  MENU_PANEL_HEADER_CLASSES,
  MENU_PANEL_TITLE_CLASSES,
  MENU_ROW_CLASSES,
  MENU_ROW_ICON_CLASSES,
  MENU_ROW_LABEL_CLASSES,
  MENU_ROW_TRAILING_CLASSES,
} from './menuStyles';
import Tippy from '@tippyjs/react';

/** FontAwesome style/prefix tokens that carry no icon name. */
const FA_NOISE = new Set([
  'fa',
  'fas',
  'far',
  'fal',
  'fab',
  'fad',
  'fak',
  'bi',
  'solid',
  'regular',
  'light',
  'brands',
  'duotone',
  'thin',
  'sharp',
]);

/**
 * FA names with no same-name Lucide export — mostly FA5→FA6 renames and
 * wording differences. Plain name matching covers the rest.
 */
const FA_LUCIDE_ALIASES: Record<string, keyof typeof LucideIcons> = {
  'address-book': 'BookUser',
  'address-card': 'ContactRound',
  'bell-slash': 'BellOff',
  bullhorn: 'Megaphone',
  'calendar-alt': 'CalendarDays',
  'calendar-check': 'CalendarCheck',
  'chart-bar': 'ChartBar',
  'chart-line': 'ChartLine',
  'check-circle': 'CircleCheck',
  'circle-check': 'CircleCheck',
  cog: 'Settings',
  cogs: 'Settings2',
  'comment-dots': 'MessageCircle',
  comments: 'MessagesSquare',
  edit: 'SquarePen',
  envelope: 'Mail',
  'exclamation-triangle': 'TriangleAlert',
  'file-alt': 'FileText',
  'file-invoice': 'ReceiptText',
  'file-lines': 'FileText',
  'file-signature': 'FileSignature',
  'folder-open': 'FolderOpen',
  gear: 'Settings',
  'graduation-cap': 'GraduationCap',
  'hand-holding-dollar': 'HandCoins',
  'hand-holding-usd': 'HandCoins',
  handshake: 'Handshake',
  'hourglass-half': 'Hourglass',
  'id-card': 'IdCard',
  'list-alt': 'List',
  'magnifying-glass': 'Search',
  'money-bill': 'Banknote',
  'money-bill-wave': 'Banknote',
  'money-check-alt': 'Banknote',
  'notes-medical': 'ClipboardPlus',
  'paper-plane': 'Send',
  'pen-to-square': 'SquarePen',
  'plane-departure': 'PlaneTakeoff',
  'sign-out-alt': 'LogOut',
  suitcase: 'Briefcase',
  'tachometer-alt': 'Gauge',
  tasks: 'ListChecks',
  thumbtack: 'Pin',
  'trash-alt': 'Trash2',
  'triangle-exclamation': 'TriangleAlert',
  times: 'X',
  'user-md': 'Stethoscope',
  'user-tie': 'UserRound',
  xmark: 'X',
};

/**
 * Resolves a stored FontAwesome class (`fas fa-piggy-bank`) to a Lucide icon.
 *
 * Forms let admins pick from the full FontAwesome catalog (see
 * `components/common/icons.ts`, ~1340 entries), so a hand-written mapping isn't
 * practical. This uses the same name-resolution the kit's `getIconComponent`
 * does: drop the FA prefixes, then look the kebab name up among lucide-react's
 * PascalCase exports — the two libraries share most icon names — with an alias
 * table for the common mismatches. Anything with no Lucide equivalent (notably
 * `fab` brand icons, which Lucide doesn't carry) falls back to `FileText`.
 *
 * Local to this menu on purpose. The other `form.iconPath` render sites
 * (SecondaryForms, EditResponse, ViewTemplate, EditForm, the mobile
 * NewRequestDropdown) still draw FontAwesome directly; lift this into a shared
 * module when the second one migrates.
 */
const lucideFromFaClass = (raw?: string | null): React.ElementType => {
  const kebab = (raw ?? '')
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.replace(/^fa-/, ''))
    .filter((token) => token && !FA_NOISE.has(token))
    .join('-');

  if (!kebab) return FileText;

  const exportName =
    FA_LUCIDE_ALIASES[kebab] ??
    kebab
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');

  const resolved = LucideIcons[exportName as keyof typeof LucideIcons];
  return typeof resolved === 'object' || typeof resolved === 'function'
    ? (resolved as React.ElementType)
    : FileText;
};

const HeaderNewRequestMenu: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { openSidebar } = useSidebarManager();

  const [isOpen, setIsOpen] = useState(false);
  const [forms, setForms] = useState<FormsResponse>();
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, setIsOpen, isOpen);

  const canUseForms = hasPermission('Form.User.View');

  useEffect(() => {
    if (!canUseForms) return;
    getListPublishedForms()
      .then((response) => {
        if (response.success) setForms(response.data);
      })
      .catch((error) =>
        console.error('Error fetching list of published forms:', error),
      );
  }, [canUseForms]);

  const userId = user.userId ?? '';

  /** Closes the panel, then runs the row's action — as kit rows do on select. */
  const select = (action: () => void) => () => {
    setIsOpen(false);
    action();
  };

  return (
    <div className="tw-relative tw-hidden md:tw-block" ref={menuRef}>
      <Tippy content={t('Nouvelle demande')}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'tw-relative tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-md tw-text-gray-600 tw-transition-colors tw-duration-150',
            isOpen
              ? 'tw-bg-gray-100 tw-text-gray-900'
              : 'hover:tw-bg-gray-50 hover:tw-text-gray-900',
          )}
        >
          <Plus size={16} strokeWidth={1.8} />
        </button>
      </Tippy>

      {isOpen && (
        <div className={MENU_PANEL_CLASSES}>
          <div className={MENU_PANEL_HEADER_CLASSES}>
            <span className={MENU_PANEL_TITLE_CLASSES}>
              {t('dashboard.new')}
            </span>
          </div>

          <div className={MENU_PANEL_BODY_CLASSES}>
            {hasPermission('Conge.View') && (
              <button
                onClick={select(() =>
                  openSidebar(CreateConge, true, { userId }),
                )}
                className={MENU_ROW_CLASSES}
              >
                <span className={MENU_ROW_ICON_CLASSES}>
                  <CalendarPlus size={16} strokeWidth={1.8} />
                </span>
                <span className="tw-flex-1 tw-min-w-0">
                  <span className={MENU_ROW_LABEL_CLASSES}>
                    {t('dashboard.new.vacation')}
                  </span>
                </span>
              </button>
            )}

            {hasPermission('DemandDocument.Profil.View.HimSelf') && (
              <button
                onClick={select(() =>
                  openSidebar(CreateDocument, true, { userId, id: 0 }),
                )}
                className={MENU_ROW_CLASSES}
              >
                <span className={MENU_ROW_ICON_CLASSES}>
                  <FileText size={16} strokeWidth={1.8} />
                </span>
                <span className="tw-flex-1 tw-min-w-0">
                  <span className={MENU_ROW_LABEL_CLASSES}>
                    {t('dashboard.new.document')}
                  </span>
                </span>
              </button>
            )}

            {hasPermission('Profile.Expenses.Edit') && (
              <button
                onClick={select(() =>
                  openSidebar(CreateExpenseForm, true, { userId }),
                )}
                className={MENU_ROW_CLASSES}
              >
                <span className={MENU_ROW_ICON_CLASSES}>
                  <Receipt size={16} strokeWidth={1.8} />
                </span>
                <span className="tw-flex-1 tw-min-w-0">
                  <span className={MENU_ROW_LABEL_CLASSES}>
                    {t('dashboard.new.note')}
                  </span>
                </span>
              </button>
            )}

            {(hasPermission('Intervention.Create') ||
              hasPermission('Profil.Intervention.Create')) && (
              <button
                onClick={select(() =>
                  openSidebar(CreateIntervention, true, {
                    Fk_DemadeBy: userId,
                  }),
                )}
                className={MENU_ROW_CLASSES}
              >
                <span className={MENU_ROW_ICON_CLASSES}>
                  <Wrench size={16} strokeWidth={1.8} />
                </span>
                <span className="tw-flex-1 tw-min-w-0">
                  <span className={MENU_ROW_LABEL_CLASSES}>
                    {t('dashboard.new.intervention')}
                  </span>
                </span>
              </button>
            )}

            {/* Published forms — icon comes from the form's stored FA class. */}
            {canUseForms &&
              forms?.forms.map((form) => {
                const FormIcon = lucideFromFaClass(form.iconPath);
                return (
                  <button
                    key={form.id}
                    onClick={select(() =>
                      openSidebar(EditResponse, true, {
                        formId: form.id,
                        responseId: 0,
                        userId,
                        withUser: true,
                      }),
                    )}
                    className={MENU_ROW_CLASSES}
                    // The kit row truncates to one line; `title` keeps long
                    // form names readable.
                    title={form.title}
                  >
                    <span className={MENU_ROW_ICON_CLASSES}>
                      <FormIcon size={16} strokeWidth={1.8} />
                    </span>
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className={MENU_ROW_LABEL_CLASSES}>
                        {form.title}
                      </span>
                    </span>
                  </button>
                );
              })}
          </div>

          {forms && forms.countSecondaryForms > 0 && (
            <div className={MENU_PANEL_FOOTER_CLASSES}>
              <button
                onClick={select(() => openSidebar(SecondaryForms, true))}
                className={MENU_ROW_CLASSES}
              >
                <span className="tw-flex-1 tw-min-w-0">
                  <span className={MENU_ROW_LABEL_CLASSES}>
                    {t('Btn.ViewForms')}
                  </span>
                </span>
                <span className={MENU_ROW_TRAILING_CLASSES}>
                  <ChevronRight size={14} strokeWidth={1.8} />
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderNewRequestMenu;
